import React, { useCallback, useRef, useState, useEffect } from 'react';
import ReactPlayer from 'react-player/youtube';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { Countdown } from '../../common/Countdown';
import { ActionButton } from '../../common/ActionButton';
import { Badge } from '../../common/Badge';
import { VideoContent, BroadcastStatus } from '../../../types/content';
import { useVideoAnalytics } from '../../../hooks/useVideoAnalytics';
import './CustomPlayer.css';
import { Popover } from '@headlessui/react';

interface CustomPlayerProps {
  video: VideoContent;
  className?: string;
  onError?: (error: Error) => void;
  autoplay?: boolean;
  onViewerCountUpdate?: (count: number) => void;
  onAddToCalendar?: () => void;
  onSetReminder?: () => void;
  onProgress?: (progress: number) => void;
  onReady?: () => void;
}

interface CaptionTrack {
  languageCode: string;
  label: string;
}

const CustomPlayer: React.FC<CustomPlayerProps> = ({
  video,
  className,
  onError,
  autoplay = false,
  onViewerCountUpdate,
  onAddToCalendar,
  onSetReminder,
  onProgress,
  onReady,
}) => {
  const playerRef = useRef<ReactPlayer>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [showControls, setShowControls] = useState(true);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<number>();
  const [qualities, setQualities] = useState<string[]>([]);
  const [currentQuality, setCurrentQuality] = useState<string>('auto');
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showCaptions, setShowCaptions] = useState(false);
  const [currentMenu, setCurrentMenu] = useState<'main' | 'quality' | 'speed' | 'captions'>('main');
  const [captionTracks, setCaptionTracks] = useState<CaptionTrack[]>([]);
  const [currentCaptionTrack, setCurrentCaptionTrack] = useState<CaptionTrack | null>(null);
  const [currentTime, setCurrentTime] = useState(0);

  const { isDark } = useTheme();
  const { isRTL } = useDirection();
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  // Add playback speed options
  const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  // Initialize video analytics with proper duration
  useVideoAnalytics({
    videoId: video.id,
    duration: duration || video.duration,
    isPlaying,
    currentTime,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update YouTube player language when it changes
  useEffect(() => {
    if (playerRef.current && video.youtube) {
      const player = playerRef.current.getInternalPlayer();
      if (player?.setOption) {
        player.setOption('captions', 'languageCode', i18n.language);
      }
    }
  }, [i18n.language, video.youtube]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handleCaptionsChange = useCallback((track: CaptionTrack | null) => {
    setCurrentCaptionTrack(track);
    if (playerRef.current) {
      const player = playerRef.current.getInternalPlayer();
      if (player?.setOption) {
        player.setOption('captions', 'track', track ? { languageCode: track.languageCode } : {});
      }
    }
  }, []);

  const handleReady = useCallback(() => {
    setIsLoading(false);
    
    if (playerRef.current) {
      const player = playerRef.current.getInternalPlayer();
      
      // For YouTube videos
      if (video.youtube && player) {
        // Set player language
        if (player.setOption) {
          player.setOption('captions', 'reload', true);
          player.setOption('captions', 'track', {});
          player.setOption('settings', 'language', i18n.language);
        }

        // Get available qualities
        if (player.getAvailableQualityLevels) {
          const availableQualities = player.getAvailableQualityLevels();
          const formattedQualities = availableQualities
            .filter((q: string) => q !== 'auto')
            .map((q: string) => {
              switch (q) {
                case 'hd1080': return '1080p';
                case 'hd720': return '720p';
                case 'large': return '480p';
                case 'medium': return '360p';
                case 'small': return '240p';
                case 'tiny': return '144p';
                default: return q.toUpperCase();
              }
            });
          if (formattedQualities.length > 0) {
            setQualities(['Auto', ...formattedQualities]);
            // Set initial quality
            const currentQuality = player.getPlaybackQuality();
            setCurrentQuality(
              currentQuality === 'auto' ? 'Auto' :
              currentQuality === 'hd1080' ? '1080p' :
              currentQuality === 'hd720' ? '720p' :
              currentQuality === 'large' ? '480p' :
              currentQuality === 'medium' ? '360p' :
              currentQuality === 'small' ? '240p' :
              currentQuality === 'tiny' ? '144p' :
              'Auto'
            );
          }
        }

        // Get playback speed
        if (player.getPlaybackRate) {
          const speed = player.getPlaybackRate();
          if (speedOptions.includes(speed)) {
            setPlaybackSpeed(speed);
          }
        }

        // Initialize captions
        try {
          // Enable captions module
          player.loadModule('captions');
          
          // Get available languages
          const getCaptions = () => {
            try {
              const tracks = player.getOption('captions', 'tracklist') || [];
              
              if (tracks && tracks.length > 0) {
                const availableTracks = tracks.map((track: { languageCode: string; displayName?: string; languageName?: string }) => ({
                  languageCode: track.languageCode,
                  label: track.displayName || track.languageName || track.languageCode
                }));
                
                setCaptionTracks(availableTracks);
                
                // Try to set English as default if available
                const defaultTrack = availableTracks.find((track: { languageCode: string }) => track.languageCode === 'en') || availableTracks[0];
                if (defaultTrack) {
                  handleCaptionsChange(defaultTrack);
                }
              }
            } catch (error) {
              console.error('Error getting caption tracks:', error);
            }
          };

          // Wait for captions module to load
          setTimeout(getCaptions, 1000);
        } catch (error) {
          console.error('Error initializing captions:', error);
        }
      }
    }
    
    onReady?.();
  }, [video.youtube, onReady, handleCaptionsChange, speedOptions, i18n.language]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  }, []);

  const handleMuteToggle = useCallback(() => {
    setIsMuted(!isMuted);
    if (isMuted) {
      setVolume(100);
    }
  }, [isMuted]);

  const handleSeekMouseDown = useCallback(() => {
    setSeeking(true);
  }, []);

  const handleSeekChange = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !duration) return;
    
    const bounds = progressBarRef.current.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const width = bounds.width;
    const percentage = Math.min(Math.max(x / width, 0), 1);
    
    setPlayed(percentage);
    playerRef.current?.seekTo(percentage, 'fraction');
  }, [duration]);

  const handleSeekMouseUp = useCallback(() => {
    setSeeking(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleVideoClick = useCallback((e: React.MouseEvent) => {
    // If we clicked the overlay div directly, toggle play/pause
    if (e.target === e.currentTarget) {
      handlePlayPause();
    }
  }, [handlePlayPause]);

  const handleControlsMouseMove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      window.clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = window.setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleControlsMouseLeave = useCallback(() => {
    if (isPlaying) {
      setShowControls(false);
    }
  }, [isPlaying]);

  const handleQualityChange = useCallback((quality: string) => {
    if (!playerRef.current) return;

    const player = playerRef.current.getInternalPlayer();
    if (player?.setPlaybackQuality) {
      player.setPlaybackQuality(quality.toLowerCase());
      setCurrentQuality(quality);
    }
  }, []);

  const handleError = useCallback((error: any) => {
    setIsLoading(false);
    onError?.(new Error(error?.message || 'Video playback error'));
  }, [onError]);

  const handleProgress = useCallback(({ playedSeconds, played }: { playedSeconds: number, played: number }) => {
    if (!seeking) {
      setCurrentTime(playedSeconds);
      setPlayed(played);
      onProgress?.(playedSeconds);
    }
  }, [seeking, onProgress]);

  const handleDuration = useCallback((newDuration: number) => {
    setDuration(newDuration);
  }, []);

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const formatTime = (seconds: number) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, '0');
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const getVideoUrl = useCallback(() => {
    if (video.youtube) {
      return `https://www.youtube-nocookie.com/embed/${video.youtube}`;
    }
    return video.url || '';
  }, [video]);

  const handlePictureInPicture = useCallback(async () => {
    try {
      const videoElement = document.querySelector('.react-player video') as HTMLVideoElement;
      if (!videoElement) return;

      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await videoElement.requestPictureInPicture();
      }
    } catch (error) {
      console.error('Picture-in-Picture failed:', error);
    }
  }, []);

  const isNativeVideo = useCallback(() => {
    return !video.youtube && video.url;
  }, [video]);

  // Add handler for playback speed
  const handleSpeedChange = useCallback((speed: number) => {
    if (!playerRef.current) return;
    setPlaybackSpeed(speed);
    const player = playerRef.current.getInternalPlayer();
    if (player?.setPlaybackRate) {
      player.setPlaybackRate(speed);
    }
  }, []);

  const renderUpcomingBanner = () => {
    if (!video.scheduledStartTime) return null;

    const timeLeft = new Date(video.scheduledStartTime).getTime() - new Date().getTime();
    if (timeLeft <= 0) return null;

    return (
      <>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-all duration-300" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white z-10">
          <div className="w-full text-center animate-fade-in">
            <div className="bg-black/40 backdrop-blur-md py-6 shadow-2xl">
              <div className="max-w-2xl mx-auto px-4">
                <div className="text-xs sm:text-sm font-medium tracking-wider uppercase mb-2 text-white/80">
                  {t('lives.starts')}
                </div>
                <Countdown
                  targetDate={new Date(video.scheduledStartTime)}
                  className="text-3xl sm:text-4xl font-bold text-status-warning drop-shadow-[0_2px_4px_rgba(0,0,0,0.35)]"
                />
              </div>
            </div>

            {(onAddToCalendar || onSetReminder) && (
              <div className={`absolute bottom-8 ${isRTL ? 'right-8' : 'left-8'} animate-slide-up`}>
                <div className="flex flex-row items-center gap-4">
                  {onAddToCalendar && (
                    <ActionButton
                      onClick={onAddToCalendar}
                      icon="ph:calendar-plus-thin"
                      label={t("lives.addToCalendar")}
                      size={isMobile ? "icon" : "extended"}
                      className="hover:scale-105 transition-transform shadow-xl bg-black/40 backdrop-blur-md"
                    />
                  )}
                  {onSetReminder && (
                    <ActionButton
                      onClick={onSetReminder}
                      icon="ph:bell-thin"
                      label={t("lives.setReminder")}
                      size={isMobile ? "icon" : "extended"}
                      className="hover:scale-105 transition-transform shadow-xl bg-black/40 backdrop-blur-md"
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const isLiveOrUpcoming = (status?: BroadcastStatus): status is 'live' | 'upcoming' => {
    return status === 'live' || status === 'upcoming';
  };

  const isUpcoming = (status?: BroadcastStatus): status is 'upcoming' => {
    return status === 'upcoming';
  };

  return (
    <div 
      ref={containerRef}
      className={`relative ${className || ''} custom-player ${isFullscreen ? 'fullscreen' : ''}`} 
      dir={isRTL ? 'rtl' : 'ltr'}
      onMouseMove={handleControlsMouseMove}
      onMouseLeave={handleControlsMouseLeave}
    >
      <ReactPlayer
        ref={playerRef}
        key={`${video.youtube}-${i18n.language}`}
        url={`https://www.youtube.com/embed/${video.youtube}`}
        className={`react-player ${isPlaying ? 'playing' : ''}`}
        width="100%"
        height="100%"
        playing={isPlaying}
        controls={false}
        volume={volume / 100}
        muted={isMuted}
        playbackRate={playbackSpeed}
        onReady={handleReady}
        onError={handleError}
        onProgress={handleProgress}
        onDuration={handleDuration}
        onEnded={handleEnded}
        config={{
          playerVars: {
            autoplay: 1,
            modestbranding: 1,
            rel: 0,
            origin: window.location.origin,
            enablejsapi: 1,
            host: window.location.origin
          }
        }}
      />
      
      {/* Transparent overlay for video area clicks */}
      <div 
        className="absolute inset-0 z-[1] cursor-pointer"
        onClick={handleVideoClick}
      />
      
      {/* Show badge at bottom corner for upcoming videos */}
      {video.broadcastStatus === 'upcoming' && (
        <div className={`absolute bottom-4 ${isRTL ? 'left-4' : 'right-4'} z-[2]`}>
          <Badge 
            type="upcoming"
            size="default"
            className="!static !w-auto !inset-auto !m-0" 
          />
        </div>
      )}

      {/* Only show controls if not upcoming */}
      {!isUpcoming(video.broadcastStatus) && (
        <div 
          className={`controls-section ${showControls ? 'opacity-100' : 'opacity-0'}`} 
          onClick={e => e.stopPropagation()}
        >
          <div className="px-4 py-0 sm:py-2">
            <div 
              className="progress-bar" 
              onClick={e => {
                e.stopPropagation();
                handleSeekChange(e);
              }} 
              ref={progressBarRef}
            >
              <div className="progress" style={{ width: `${played * 100}%` }} />
            </div>
            
            <div className="flex items-center gap-4 h-10">
              <div className={`flex items-center gap-4 ${isRTL && video.youtube ? 'mr-[120px]' : ''}`}>
                <button 
                  className="control-button"
                  onClick={e => {
                    e.stopPropagation();
                    handlePlayPause();
                  }}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  <Icon 
                    icon={isPlaying ? "ph:pause-thin" : "ph:play-thin"} 
                    className="w-5 h-5" 
                  />
                </button>

                <div className="flex items-center gap-1 group">
                  <button 
                    className="control-button"
                    onClick={e => {
                      e.stopPropagation();
                      handleMuteToggle();
                    }}
                    aria-label={isMuted ? 'Unmute' : 'Mute'}
                  >
                    <Icon 
                      icon={
                        isMuted || volume === 0
                          ? "ph:speaker-slash-thin"
                          : volume < 50
                          ? "ph:speaker-low-thin"
                          : "ph:speaker-high-thin"
                      } 
                      className="w-5 h-5" 
                    />
                  </button>
                  <div className="w-0 group-hover:w-20 overflow-hidden transition-all duration-300">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={isMuted ? 0 : volume}
                      onChange={(e) => handleVolumeChange(Number(e.target.value))}
                      className="volume-slider ml-2"
                      style={{ '--value': `${isMuted ? 0 : volume}%` } as React.CSSProperties}
                      aria-label="Volume"
                    />
                  </div>
                </div>

                <div className="text-white/80 text-xs font-medium tracking-wide select-none">
                  <span>{formatTime(duration * played)}</span>
                  <span className="mx-1.5 text-white/40">/</span>
                  <span className="text-white/60">{formatTime(duration)}</span>
                </div>
              </div>

              <div className="flex-grow" />

              {/* Settings and fullscreen buttons - moved inward only for YouTube videos */}
              <div className={`flex items-center gap-3 ${video.youtube ? 'mr-[120px]' : ''}`}>
                {/* Live badge in controls - moved inside settings group for RTL */}
                {video.broadcastStatus === 'live' && (
                  <div className="inline-flex items-center">
                    <Badge 
                      type="live"
                      size="default"
                      className="scale-90 !relative !top-auto !right-auto !bottom-auto !left-auto transform-gpu" 
                    />
                  </div>
                )}

                <Popover className="relative">
                  {({ open, close }: { open: boolean; close: () => void }) => (
                    <>
                      <Popover.Button className="control-button hidden sm:flex">
                        <Icon icon="ph:gear-thin" className="w-5 h-5" />
                      </Popover.Button>
                      
                      <Popover.Panel className={`absolute top-full mt-3 sm:top-auto sm:bottom-full sm:mt-0 sm:mb-2 ${isRTL ? 'left-0' : 'right-0'} z-50 min-w-[200px] bg-black rounded-lg shadow-lg overflow-hidden`}>
                        {currentMenu === 'main' && (
                          <div className="py-1">
                            {qualities.length > 0 && (
                              <button
                                className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90"
                                onClick={() => setCurrentMenu('quality')}
                              >
                                <span>{t('video.settings.quality')}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-white/60">{currentQuality}</span>
                                  <Icon icon={isRTL ? "ph:caret-left-thin" : "ph:caret-right-thin"} className="w-4 h-4" />
                                </div>
                              </button>
                            )}

                            <button
                              className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90"
                              onClick={() => setCurrentMenu('speed')}
                            >
                              <span>{t('video.settings.speed')}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-white/60">{playbackSpeed}x</span>
                                <Icon icon={isRTL ? "ph:caret-left-thin" : "ph:caret-right-thin"} className="w-4 h-4" />
                              </div>
                            </button>

                            {video.youtube && captionTracks.length > 0 && (
                              <button
                                className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90"
                                onClick={() => setCurrentMenu('captions')}
                              >
                                <span>{t('video.settings.captions')}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-white/60">
                                    {currentCaptionTrack ? currentCaptionTrack.label : t('video.settings.off')}
                                    </span>
                                  <Icon icon={isRTL ? "ph:caret-left-thin" : "ph:caret-right-thin"} className="w-4 h-4" />
                                </div>
                              </button>
                            )}
                          </div>
                        )}

                        {currentMenu === 'quality' && (
                          <div className="py-1">
                            <button
                              className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center gap-2 text-white/90"
                              onClick={() => setCurrentMenu('main')}
                            >
                              <Icon icon={isRTL ? "ph:caret-right-thin" : "ph:caret-left-thin"} className="w-4 h-4" />
                              <span>{t('video.settings.quality')}</span>
                            </button>
                            <div className="h-px bg-white/10 my-1" />
                            <button
                              className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90 ${
                                currentQuality === t('video.settings.auto') ? 'text-primary' : ''
                              }`}
                              onClick={() => {
                                handleQualityChange('auto');
                                close();
                              }}
                            >
                              <span>{t('video.settings.auto')}</span>
                              {currentQuality === t('video.settings.auto') && (
                                <Icon icon="ph:check-thin" className="w-4 h-4" />
                              )}
                            </button>
                            {qualities.map((quality) => (
                              <button
                                key={quality}
                                className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90 ${
                                  currentQuality === quality ? 'text-primary' : ''
                                }`}
                                onClick={() => {
                                  handleQualityChange(quality);
                                  close();
                                }}
                              >
                                <span>{quality}</span>
                                {currentQuality === quality && (
                                  <Icon icon="ph:check-thin" className="w-4 h-4" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {currentMenu === 'speed' && (
                          <div className="py-1">
                            <button
                              className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center gap-2 text-white/90"
                              onClick={() => setCurrentMenu('main')}
                            >
                              <Icon icon={isRTL ? "ph:caret-right-thin" : "ph:caret-left-thin"} className="w-4 h-4" />
                              <span>{t('video.settings.speed')}</span>
                            </button>
                            <div className="h-px bg-white/10 my-1" />
                            {speedOptions.map((speed) => (
                              <button
                                key={speed}
                                className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90 ${
                                  playbackSpeed === speed ? 'text-primary' : ''
                                }`}
                                onClick={() => {
                                  handleSpeedChange(speed);
                                  close();
                                }}
                              >
                                <span>{speed === 1 ? t('video.settings.normal') : `${speed}x`}</span>
                                {playbackSpeed === speed && (
                                  <Icon icon="ph:check-thin" className="w-4 h-4" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        {currentMenu === 'captions' && (
                          <div className="py-1">
                            <button
                              className="w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center gap-2 text-white/90"
                              onClick={() => setCurrentMenu('main')}
                            >
                              <Icon icon={isRTL ? "ph:caret-right-thin" : "ph:caret-left-thin"} className="w-4 h-4" />
                              <span>{t('video.settings.captions')}</span>
                            </button>
                            <div className="h-px bg-white/10 my-1" />
                            <button
                              className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90 ${
                                !currentCaptionTrack ? 'text-primary' : ''
                              }`}
                              onClick={() => {
                                handleCaptionsChange(null);
                                close();
                              }}
                            >
                              <span>{t('video.settings.off')}</span>
                              {!currentCaptionTrack && (
                                <Icon icon="ph:check-thin" className="w-4 h-4" />
                              )}
                            </button>
                            {captionTracks.map((track) => (
                              <button
                                key={track.languageCode}
                                className={`w-full px-4 py-2 text-sm text-left hover:bg-white/10 transition-colors flex items-center justify-between text-white/90 ${
                                  currentCaptionTrack?.languageCode === track.languageCode ? 'text-primary' : ''
                                }`}
                                onClick={() => {
                                  handleCaptionsChange(track);
                                  close();
                                }}
                              >
                                <span>{track.label}</span>
                                {currentCaptionTrack?.languageCode === track.languageCode && (
                                  <Icon icon="ph:check-thin" className="w-4 h-4" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </Popover.Panel>
                    </>
                  )}
                </Popover>

                {isNativeVideo() && (
                  <button 
                    className="control-button hidden sm:flex"
                    onClick={handlePictureInPicture}
                    aria-label="Picture in Picture"
                  >
                    <Icon icon="ph:picture-in-picture-thin" className="w-5 h-5" />
                  </button>
                )}

                <button 
                  className="control-button"
                  onClick={toggleFullscreen}
                  aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                >
                  <Icon 
                    icon={isFullscreen ? "ph:corners-in-thin" : "ph:corners-out-thin"} 
                    className="w-5 h-5" 
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {video.broadcastStatus === 'upcoming' && renderUpcomingBanner()}
    </div>
  );
};

export default CustomPlayer; 